import {
  workV4Query,
  getValueFromHTMLStringByAttribute,
  IConversationMessage,
  IWorkUser,
} from '@diana/core';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { WORK_PAGE_NS } from '../i18n';
import { CommentsRichTextEditor } from './CommentsRichTextEditor';
import { dataMentionAttribute, dataMentionUserIdAttribute } from './Support';

export interface ICommentsEditCommentBoxProps {
  workId: string;
  message: IConversationMessage;
  onCancel: () => void;
  users: IWorkUser[];
}

export function CommentsEditCommentBox(props: ICommentsEditCommentBoxProps) {
  const { onCancel, message, workId, users } = props;

  const { t } = useTranslation(WORK_PAGE_NS);

  const messageId = message.id;

  const updateMessageQuery = workV4Query.updateWorkConversationMessage({ workId, messageId });
  const { mutateAsync: updateMessageFn, isPending: isUpdatingMessage } = useMutation({
    mutationKey: updateMessageQuery.key,
    mutationFn: updateMessageQuery.fn,
    onSuccess: () => onCancel(),
  });

  const onSaveCommentChanges = useCallback(
    async (text: string) => {
      try {
        const users = getValueFromHTMLStringByAttribute({
          html: text,
          querySelector: `[${dataMentionAttribute}][${dataMentionUserIdAttribute}]`,
          htmlAttribute: dataMentionUserIdAttribute,
          unique: true,
        });

        await updateMessageFn({
          message: {
            messageText: text,
            mentionedUsers: users,
          },
        });
      } catch (e) {}
    },
    [updateMessageFn],
  );

  return (
    <CommentsRichTextEditor
      editMode
      users={users}
      data={message.messageText}
      cancelButton={{
        fn: onCancel,
        label: t('commentsDrawer.cancel'),
      }}
      sendButton={{
        fn: (text) => {
          if (isUpdatingMessage) return;
          onSaveCommentChanges(text);
        },
        isLoading: isUpdatingMessage,
        label: t('commentsDrawer.save'),
      }}
    />
  );
}
